import React, { useState } from 'react';
import { Character } from '../types';
import { charactersAPI } from '../services/api';
import './CreateCharacterModal.css';

// Declare the global showToast function
declare global {
  interface Window {
    showToast: (message: string, type?: 'success' | 'error' | 'info' | 'warning', duration?: number) => void;
  }
}

interface CreateCharacterModalProps {
  onClose: () => void;
  onCharacterCreated: (character: Character) => void;
}

const CreateCharacterModal: React.FC<CreateCharacterModalProps> = ({
  onClose,
  onCharacterCreated
}) => {
  const [formData, setFormData] = useState({
    name: '',
    article: '',
    image: null as File | null
  });
  const [imagePreview, setImagePreview] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setProgress(0);
    setProgressMessage('Preparing to create character...');

    try {
      // Step 1: Validate form data
      setProgress(10);
      setProgressMessage('Validating form data...');
      await new Promise(resolve => setTimeout(resolve, 200));

      // Step 2: Prepare form data
      setProgress(25);
      setProgressMessage('Preparing data for upload...');
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('article', formData.article);
      
      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }
      
      await new Promise(resolve => setTimeout(resolve, 300));

      // Step 3: Upload to server
      setProgress(50);
      setProgressMessage('Creating character on server...');
      const newCharacter = await charactersAPI.createWithImage(formDataToSend);
      
      // Step 4: Finalize
      setProgress(90);
      setProgressMessage('Finalizing creation...');
      await new Promise(resolve => setTimeout(resolve, 200));
      
      setProgress(100);
      setProgressMessage('Character created successfully!');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      onCharacterCreated(newCharacter);
      
      // Close modal first
      onClose();
      
      // Show success toast after modal closes
      setTimeout(() => {
        if (window.showToast) {
          window.showToast('Character created successfully!', 'success');
        } else {
          console.log('Toast function not available, using alert instead');
          alert('Character created successfully!');
        }
      }, 300);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create character');
      setProgressMessage('Failed to create character');
      // Show error toast after a short delay
      setTimeout(() => {
        if (window.showToast) {
          window.showToast('Failed to create character', 'error');
        } else {
          console.log('Toast function not available, using alert instead');
          alert('Failed to create character');
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, image: file }));
    
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview('');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>
        <h2>Create New Character</h2>
        
        <form onSubmit={handleSubmit}>
          {loading && (
            <div className="progress-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <div className="progress-message">{progressMessage}</div>
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              name="name"
              id="char-name"
              required
              value={formData.name}
              onChange={handleInputChange}
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="article">Article</label>
            <textarea
              name="article"
              id="char-article"
              required
              value={formData.article}
              onChange={handleInputChange}
              rows={4}
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="image">Image</label>
            <input
              type="file"
              name="image"
              id="char-image"
              accept="image/*"
              onChange={handleFileChange}
              disabled={loading}
            />
            {imagePreview && (
              <div className="image-preview">
                <img src={imagePreview} alt="Preview" />
              </div>
            )}
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-actions">
            <button type="submit" className="btn btn--green" disabled={loading}>
              {loading ? 'Creating...' : 'Save'}
            </button>
            <button type="button" className="btn btn--gray" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCharacterModal; 